import React, { useEffect } from "react";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import { navigate, Link } from "gatsby";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import {
    container,
    sectionHeader,
    sectionHeaderTitle,
    sectionHeaderDescription,
    sectionHeaderButton,
    looksGrid,
    lookCard,
    lookThumb,
    lookThumbImg,
    lookMeta,
    lookDate,
    lookTag,
    info,
    error,
    accountContent,
} from "./stylizations.module.scss";
import { mainGrid } from "../../../styles/grid.module.scss";
import { TStatus } from "../../../models/status.model";
import { IStylization } from "../../../models/stylization.model";
import { getAbsolutePath } from "../../utility/routes.utility";
import {
    selectStylizationsItems,
    selectStylizationsStatus,
} from "../../../redux/stylizations/stylizations.selectors";
import {
    clearStylizationsAction,
    getStylizationsAction,
} from "../../../redux/stylizations/stylizations.actions";
import { clearSingleStylizationAction } from "../../../redux/single-stylization/single-stylization.actions";
import { selectSingleStylizationStatus } from "../../../redux/single-stylization/single-stylization.selectors";

import Loader from "../../../components/atoms/loader";

const Stylizations: React.FC = () => {
    const { t } = useI18next();
    const dispatch = useDispatch();
    const status: TStatus = useSelector(selectStylizationsStatus);
    const singleStylizationStatus: TStatus = useSelector(selectSingleStylizationStatus);
    const stylizations: IStylization[] = useSelector(selectStylizationsItems);
    const API_NEW_URL = process.env.API_NEW_URL;

    const showLoader =
        status === "loading" || status === "idle" || singleStylizationStatus === "loading";

    const handleAddStylization = async () => {
        await navigate(getAbsolutePath("GENERATOR_NEW_STYLIZATION"));
    };

    useEffect(() => {
        if (singleStylizationStatus === "success") {
            dispatch(clearStylizationsAction());
            dispatch(getStylizationsAction());
        }
    }, [singleStylizationStatus]);

    useEffect(() => {
        dispatch(getStylizationsAction());
        return () => {
            dispatch(clearStylizationsAction());
            dispatch(clearSingleStylizationAction());
        };
    }, [dispatch]);

    const getStylizationImage = (stylization: IStylization): string => {
        if (stylization.guid) {
            return `${API_NEW_URL}/files/download/${stylization.guid}.png`;
        }
        if (stylization.imgUrl) {
            return stylization.imgUrl;
        }
        if (stylization.products && stylization.products.length > 0) {
            return stylization.products[0].imgUrl || "";
        }
        return "";
    };

    const getStylizationTag = (stylization: IStylization): string => {
        if (stylization.occasion && stylization.occasion.label) {
            return stylization.occasion.label;
        }
        if (stylization.name) {
            return stylization.name;
        }
        return "";
    };

    return (
        <div className={`${mainGrid} ${container}`}>
            <div className={accountContent}>
                {showLoader && <Loader position="absolute" />}
                
                <div className={sectionHeader}>
                    <div>
                        <h1 className={sectionHeaderTitle}>Twoje stylizacje</h1>
                        <p className={sectionHeaderDescription}>
                            Przeglądaj zapisane zestawy ubrań i wracaj do ulubionych.
                        </p>
                    </div>
                    <button 
                        className={sectionHeaderButton} 
                        type="button"
                        onClick={handleAddStylization}
                    >
                        Dodaj stylizację
                    </button>
                </div>

                {status === "fail" && (
                    <p className={`${info} ${error}`}>{t("stylizations.fail")}</p>
                )}
                {status === "success" && stylizations.length === 0 && (
                    <p className={info}>{t("stylizations.empty")}</p>
                )}
                {status === "success" && stylizations.length > 0 && (
                    <div className={looksGrid}>
                        {stylizations
                            .filter(stylization => stylization.autoSave == 0)
                            .map((stylization) => {
                                const imageUrl = getStylizationImage(stylization);
                                const tag = getStylizationTag(stylization);
                                const date = moment(stylization.createdAt * 1000).format('DD.MM.YYYY');
                                
                                return (
                                    <Link
                                        key={stylization.stylizationId}
                                        to={`${getAbsolutePath("GENERATOR_STYLIZATION")}/${stylization.stylizationId}`}
                                        className={lookCard}
                                    >
                                        <div className={lookThumb}>
                                            {imageUrl ? (
                                                <img 
                                                    className={lookThumbImg}
                                                    src={imageUrl} 
                                                    alt={stylization.name || "Stylizacja"} 
                                                />
                                            ) : (
                                                <div className={lookThumbImg} style={{ background: '#f0f0f0' }} />
                                            )}
                                        </div>
                                        <div className={lookMeta}>
                                            <span className={lookDate}>{date}</span>
                                            {tag && <span className={lookTag}>{tag}</span>}
                                        </div>
                                    </Link>
                                );
                            })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Stylizations;
